// ** React Imports
import { useContext, useState } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import loadable from "@loadable/component";

// ** Custom Components
import Dropdown from "components/layout/Dropdown";
import Block from "components/layout/Block";
import Loader from "components/layout/Loader";

// ** Search Components
import SearchButton from "./components/SearchButton";
const SearchContent = loadable(() => import("./components/SearchContent"), {
	ssr: false,
});

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";
import useBreakpointWidth from "hooks/useBreakpointWidth";
import useDetectKeyboardOpen from "hooks/useDetectKeyboardOpen";
import useMobileDetect from "hooks/useMobileDetectHook";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";
import { DropdownTriggerContext } from "components/layout/Dropdown/DropdownTriggerContext";

// ** Utils & Helpers
import { overflowChanger } from "helpers/overflowChanger";

// ** Constants
import { topHeaderHeight } from "styles/declares";

// #####################################################

const MAX_DESKTOP_WIDTH = "425px";

// #####################################################

const DropdownSearch = () => {
	const { isPhone, isTablet } = useContext(deviceTypeContext);
	const mobileDetect = useMobileDetect();
	const isIos = mobileDetect.isIos();

	const isClient = useSelector((state) => state.globalCommon.isClient);
	const isUserFrozen = useSelector((state) => state.globalUser.frozen);

	const breakpointWidth = useBreakpointWidth();

	const { isOpen, close, toggle } = useOpenClose();
	const { isKeyboardOpen, visualViewportHeight } = useDetectKeyboardOpen();
	const [isExtended, setIsExtended] = useState(false);

	const closeAndCollapse = ({ insideUseEffect = false } = {}) => {
		if (isExtended) {
			setIsExtended(false);
		}
		if (insideUseEffect) {
			close();
		} else {
			close(() => {
				overflowChanger({ conditionName: "ifIsHidden" });
			});
		}
	};

	const extendedToggle = () => {
		toggle(() => {
			if (!isOpen && isPhone) {
				overflowChanger({ conditionName: "ifIsNotHidden" });
			} else if (isOpen) {
				overflowChanger({ conditionName: "ifIsHidden" });
			}
		});
	};

	const useLayerOptions = {
		onOutsideClick: () => {
			!isPhone && typeof close === "function" && closeAndCollapse();
		},
		placement: "bottom-center",
		triggerOffset: 10,
		container: isClient ? "app" : "root",
	};

	const customLayerStyle = {
		position: "fixed",
		width: isPhone ? "calc(100% - 1px)" : "100%",
		maxWidth: isPhone ? "calc(100% - 1px)" : MAX_DESKTOP_WIDTH,
		overflowY: "auto",
		right: isPhone ? `1px` : `calc((100% - ${breakpointWidth}px)/2)`,
		left: "unset",
		top: isPhone
			? `${topHeaderHeight.mobile}px`
			: isTablet
				? `${topHeaderHeight.tablet}px`
				: `${topHeaderHeight.desktop}px`,
		maxHeight: `calc((100vh - ${topHeaderHeight.desktop}px) - var(--size-grid))`,
		zIndex: 498,
		borderTopRightRadius: 0,
		borderTopLeftRadius: 0,
	};

	// #####################################################

	return (
		<Dropdown
			isOpen={isOpen}
			close={(insideUseEffect) => {
				closeAndCollapse(insideUseEffect);
			}}
			useLayerOptions={useLayerOptions}
			customLayerStyle={customLayerStyle}
			withMobileVersion={true}
			isParentComponent={true}
			isKeyboardOpen={isKeyboardOpen}
			visualViewportHeight={visualViewportHeight}
			customMobileLayerStyle={{
				display: "flex",
				flexDirection: "column",
			}}
			isSingleDropdown={true}
			menuContent={() => (
				<SearchContent
					fallback={
						<Block flex justifyCenter alignCenter height={40}>
							<Loader size={20} />
						</Block>
					}
					isPhone={isPhone}
					isIos={isIos}
					close={closeAndCollapse}
					isKeyboardOpen={isKeyboardOpen}
					visualViewportHeight={visualViewportHeight}
					setIsExtended={setIsExtended}
					isExtended={isExtended}
				/>
			)}
		>
			<DropdownTriggerContext.Consumer>
				{({ triggerProps }) => (
					<SearchButton
						triggerProps={triggerProps}
						active={isOpen}
						toggle={extendedToggle}
						userFrozen={isUserFrozen}
						startPreload={() => {
							SearchContent.preload();
						}}
					/>
				)}
			</DropdownTriggerContext.Consumer>
		</Dropdown>
	);
};

export default DropdownSearch;

// #####################################################
